import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { MeetingNote, NoteFilter } from '../documentations/api/meeting-notes.js';
import { QuarterNoteQuestion } from '../documentations/api/QuarterNoteQuestion.js';
import { QuarterNoteQuestionFilter } from '../documentations/api/QuarterNoteQuestionFilter.js';
import { StudentContactNote } from '../documentations/entities/StudentContactNote.js';
import type { BundeslandAspect } from '../master-data-management/aspects/BundeslandAspect.js';
import type { ZipCityAspect } from '../master-data-management/aspects/ZipCityAspect.js';
import { Partner } from '../master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';

//! this needs to be a registerSubscription, because otherwise data view will ignore this. But it's actually a self-built query
const _queryMeetingNotesFiltered = registerSubscription({
  operationName: 'queryMeetingNotesFiltered',
  type: () => [MeetingNote],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => NoteFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: NoteFilter) => (gqlFields: BaseQLSelectionSet<MeetingNote>) => {
    const state = filter.getFilterAspect<BundeslandAspect>('state');
    const zipCity = filter.getFilterAspect<ZipCityAspect>('zipCityOfPartner');

    const needKooperationsPartnerLookup = state?.value || zipCity?.zipCity?.city || zipCity?.zipCity?.zip;

    const aggregation = [
      {
        $lookup: {
          from: ChancenwerkUser._collectionName,
          as: 'creatorUsers',
          foreignField: '_id',
          localField: 'creatorID',
        },
      },
      { $addFields: { creator: { $arrayElemAt: ['$creatorUsers', 0] } } },

      ...(needKooperationsPartnerLookup
        ? [
            {
              $lookup: {
                from: Partner._collectionName,
                as: 'kooperationsPartner',
                foreignField: '_id',
                localField: 'kooperationspartnerID',
              },
            },

            // Bundeland check
            ...(state?.value ? [{ $match: { 'kooperationsPartner.bundesland': state.value } }] : []),

            // City check
            ...(zipCity?.zipCity?.city
              ? [{ $match: { 'kooperationsPartner.address.zipCity.city': zipCity.zipCity.city } }]
              : []),

            // Zip check
            ...(zipCity?.zipCity?.zip
              ? [{ $match: { 'kooperationsPartner.address.zipCity.zip': zipCity.zipCity.zip } }]
              : []),
          ]
        : []),

      { $addFields: { creator_name: { $concat: ['$creator.vorname', '$creator.nachname'] } } },
      { $match: filter.getMongoFilter() },
      { $sort: filter.getMongoOptions().sort ?? { _id: -1 } },
    ];

    return from(publishFilter(MeetingNote._class)).pipe(
      switchMap(async filter => {
        const MeetingNotesCol = await getRawCollection(MeetingNote._collectionName);
        return MeetingNotesCol.aggregate([
          { $match: filter },
          ...aggregation,
          { $skip: skip },
          { $limit: limit },
        ]).toArray();
      }),
    );
  },
});

function _getQuestionMap() {
  const questionMap = {};

  questionMap['Schüler:innen Akquise'] = [
    'Wann fand für welche Stufe die letzte Akquise statt?',
    'Für welche Stufe fand aus welchem Grund keine Akquise',
  ];

  questionMap['Angaben zum CW-Image'] = [
    'Welchen Eindruck hat die Schule/AP von CW?',
    'Wie fühlt sich das studentische Team an der Schule?',
    'Gab es in der Vergangenheit Vorfälle, die Einfluss auf unsere Kooperation haben?',
    'Gab es Situation, durch die wir besonders positiv an der Schule aufgefallen sind?',
    'Kontakt zur AP, falls vorhanden: Wie funktioniert dieser?',
  ];

  questionMap['Angaben zum Schulteam'] = [
    'Wie funktioniert das Team?',
    'Funktionieren bspw. Absprachen und Vertretungen?',
    'Welche Kommunikationskanäle werden genutzt und welche funktionieren gut?',
    'Ist es schwer bspw. einen Termin zu finden für eine Teamsitzung?',
    'Wie regelmäßig finden Teamsitzungen statt?',
    'Finden Teamsitzungen digital/und oder in präsenz statt?',
    'Wie oft haben Teamsitzungen bisher im laufenden Schuljahr stattgefunden? Bitte nenne Daten',
    'Wie ist die Bereitschaft zusätzliche Termine wahrzunehmen?',
    'Wie qualitativ ist die Teilnahme einzelner ÜL an diesen Terminen?',
    'Wie ist das Verhältnis zwischen STB und ÜL/SchuKo?',
    'Wie stabil ist das Team?',
    'Gibt es eine hohe Flukuation?',
    'Wie funktionieren Absprachen zu Abwesenheiten? Werden sie bei Möglichkeit frühzeitig bekannt gegeben?',
  ];

  questionMap['Digitale Anmeldung'] = [
    'Ist SchuKo darin eingearbeitet?',
    'Wird die digitale Anmeldung umgesetzt?',
    'Hast du bei der CHANCENreise den QR-Code für Akquise genutzt?',
    'Konntest du die digitale Anmeldung der OS umsetzen? Wenn nein, warum?',
  ];

  questionMap['Einarbeitung ÜL'] = [
    'Hast du einen realistischen Überblick welche ÜL an dem Onboarding teilgenommen haben?',
    'Schließen die ÜL die Einarbeitung im Selbststudium tatsächlich ab?',
    'Wurde ein Feedback-Gespräch geführt?',
    'Funktioniert die Aufnahme in die WhatsApp Gruppe? Wie gut und wie schnell funktioniert das?',
    'Bist du als STB in allen WhatsApp Gruppen dabei? ',
  ];

  questionMap['Elternsprechtag'] = [
    'Was haben wir gemacht?',
    'Was lief gut?',
    'Was lief nicht gut?',
    'Termin',
    'Was ist die allgemeine Erfahrung mit unterschiedlichen Akquiseformaten an der Schule?',
    'Welche Formate haben sich erfolgreich durchgesetzt und welche lohnen sich aus welchen Gründen nicht?',
  ];

  questionMap['Materiallagerung'] = [
    'Haben wir einen abschließbaren Materialschrank?',
    'Wo werden die Materialien von Chancenwerk in der Schule gelagert?',
    'Wie gut ist der Materialschrank strukturiert? Sollte die Ordnung verbessert werden?',
  ];

  questionMap['Monitoring'] = [
    'Wie viele US haben teilgenommen?',
    'Wie viele OS haben teilgenommen?',
    'Wurden die Ergebnisse an alle Teammitlgieder versendet?',
  ];

  questionMap['OS-Akquise'] = [
    'Wurde Akquise gemacht?',
    'Wenn ja, wann?',
    'In welcher Stufe?',
    'Was wurde genau gemacht (Rundgang/Plenum/Briefe über die Lehrkräfte usw.)?',
    'Wer hat das gemacht (OS-Coach/SchuKo/LeKo/PL/STB)?',
    'Wurde die CHANCENreise berücksichtigt oder das alte Format?',
    'Haben wir einen OS-Coach an der Schule? ',
    'Wenn nein, warum? Welche Herausforderungen gab es bei der Suche des OS-Coaches?',
    'Wenn ja, hat die Kommunikation zwischen OS-Coach und AP zwecks Terminabsprachen funktioniert?',
    'Wie stellst du sicher, dass der OS-Coach regelmäßig an den Schulungen der CHANCENakademie teilnimmt?',
    'Was passiert, wenn es keinen OS-Coach gibt? Wer übernimmt dann die Akquise?',
    'Hat ein Kick-Off stattgefunden, wenn ja, wann und in welcher Form (wurden Prozesse von der CHANCENreise berücksichtigt oder das alte Format)?',
    'Wurden Workshops angeboten und wenn ja, welche? Falls nicht, nennt bitte die Gründe.',
    'Gibt es Intensivkurse vor Ort?',
    'Wie viele digitale IKs finden pro Schule statt?',
    'Gibt es ehrenamtliche OS?',
    'Wenn es keine Akquise gab, dann nennt bitte die Gründe.',
  ];

  questionMap['OS-Zertifikate'] = ['Bekommen in diesem Schulhalbjahr OS auch Zertifikate?'];

  questionMap['Produkt wird gut genutzt/umgesetzt'] = [
    'CHANCENkurve',
    'CHANCENboard',
    'CHANCENreise',
    'CHANCENcampus',
    'CHANCENlogistik',
    'CHANCENakademie',
    'CHANCENhefte',
    'CHANCENabschluss',
  ];

  questionMap['Projekte'] = [
    'Bitte mache Angaben zur Einführung dieser Projekte an der jeweligen Koop. Schule.',
    'Infos zur Einarbeitung, Ablauf, Herausforderungen, Erfolg, Umsetzung, Wirksamkeit, realistische Planbarkeit',
  ];

  questionMap['Schulbesuche'] = ['Wie regelmäßig triffst du eine Schule?'];

  questionMap['US-Akquise'] = [
    'Wurde Akquise bei den US gemacht?',
    'Wenn ja, welche Jahrgänge wurden angesprochen?',
    'Wer hat die Akquise übernommen (STB, PL, SchuKo, LeKo, IKL)?',
    'In welcher Form wurden die US angesprochen (Rundgänge, Infozettel inkl. Rückmeldebörgen über Lehrer:innen, Klassenpflegschaftssitzungen)?',
    'Wenn es keine Akquise gab, dann nennt bitte die Gründe.',
  ];

  questionMap['Zusammenarbeit mit Schule'] = [
    'Wie bewertest du die Zusammenarbeit mit dem Sekretariat?',
    'Gibt es an der Schule ein Chancenwerkfach? Wie wird dieses genutzt?',
    'Wer kümmert sich darum, dass immer genug leere Anmeldeformulare im Sekretariat hinterlegt sind?',
    'Wenn von der Schule ein Lernfördertermin kurzfristig nicht stattfinden kann (z.B. hitzefrei, Unwetterwarnung), wie verläuft die Kommunikation und sollte diese verbessert werden?',
    'Besteht Kontakt zur Schulsozialarbeit und wenn ja, wie würdest du diesen beschreiben?',
    'Wie ist der Kontakt zum Hausmeister?',
    'Gab es in letzter Zeit Kontakt zu weiteren Personen in der Schule?',
  ];

  questionMap['Wie werden die Ergebnisse an die Teammitglieder versendet?'] = [
    'Wurden die Ergebnisse in einer Teamsitzung besprochen?',
    'Wie ist der Umgang mit geringer Teilnehmerzahlen?',
    'Gibt es Möglichkeiten Ergebnisse auch mit AP zu besprechen?',
    'Welche Learnings können herausgezogen werden? Welche Maßnahmen können ergriffen werden?',
  ];

  questionMap['Zusammenarbeit SchuKo'] = [
    'Wie funktioniert die Zusammenarbeit?',
    'Wie ist die Errreichbarkeit und "Wartezeit" für Rückmeldungen?',
    'Wird das Diensthandy in der Zusammenarbeit genutzt?',
    'Wird das Diensthandy in der Zusammenarbeit mit der Elternarbeit vom SchuKo genutzt?',
    'Laufen Kommunikation und das Versenden wichtiger Informationen über die Dienst-E-Mail-Adresse?',
    'Übernimmt die SchuKo zusätzliche Termine im Team?',
    'Funktioniert die Kommunikation zwischen SchuKo und Schulteam (LeKo, IKL, AP)?',
    'Wie empfindest du die Teamstärke in diesem Team?',
    'Gibt/gab es Spannungen, die Einfluss auf die Zusammenarbeit haben/hatten?',
    'Gibt es Dinge, die besonders gut funktionieren?,',
  ];

  questionMap['CHANCENakademie'] = [
    'Wie stellst du sicher, dass die ÜL regelmäßig an den Veranstaltungen der CHANCENakademie teilnehmen?',
  ];

  return questionMap;
}

function _getQuestionMapByQuartal() {
  const questionMap = [{}, {}, {}, {}];

  // 1

  questionMap[0]!['Lehrerkonferenzen'] = [
    'Ist die Teilnahme möglich?',
    'Wenn ja, in wlechem Format fand die Teilnahme statt?',
  ];

  questionMap[0]!['Klassenpflegschaftsitzung (Eltern)'] = [
    'Welche Jahrgangsstufen wurden angesprochen?',
    'In welchen Format wurden die Jahrgangsstufen angesprochen?',
  ];

  questionMap[0]!['OS Akquise'] = [
    'Wann wurden die OS im Zuge der Chancenreise angesprochen?',
    'Wann fand die Kick-Off Veranstatung statt?',
    'Wann fand der erste US-Workshop statt?',
    'Wann fand der zweite US-Workshop statt?',
  ];

  questionMap[0]!['US Akquise'] = [
    'Welche Jahrgangsstufen wurden angesprochen?',
    'In welchem Format wurden diese angesprochen?',
  ];

  questionMap[0]!['Start der LF'] = ['Wann beginnt die LF?'];

  questionMap[0]!['Chancenwerk Elternabend'] = [
    'Besteht die Möglichkeit eines Chancenwerk-Elternabends?',
    'Wenn ja, wann wird dieser statt finden?',
    'Wie gestaltet ihr diesen Abend?',
  ];

  questionMap[0]!['Schuljahresstart'] = [
    'Wann beginnt die LF?',
    'Hat die SchuKo an Digitalen SchuKo Treffen teilgenommen, sodass Sie über wichtigsten Punkte zum Start informiert ist?',
    'Haben wir als Chancenwerk einen Auftritt auf der Schulehomepage?',
  ];

  questionMap[0]!['CHANCENREISE'] = [
    'Wer übernimmt die Rolle des OS-Coaches?',
    'Welche IK sind entstanden? Bitte gebe an: Format (digital, Präsenz), Fächer, Startdatum, Anzahl der teiilnehmenden Jugendlichen',
  ];

  // 2

  questionMap[1]!['Elternsprechtag'] = [
    'Wann fand der Elternsprechtag statt?', //
    'In welchen Format fand der Elternsprechtag statt?',
  ];

  questionMap[1]!['Akquise: ggf. Leherkonferenz'] = [
    'Nach den Herbstferein gibt es Lehrerkonferenzen. Zeigt Chancenwerk dort Präsenz?', //
    'Wenn ja, wie?',
  ];

  // questionMap[1]!['Akquise: ggf. Klassenrundgänge'] = [
  //   '??', //
  // ];

  questionMap[1]!['Tag der offenen Tür'] = [
    'Wann findet der Tag der offenen Tür statt?', //
    'Wie zeigt Chancenwerk Präsenz?',
  ];

  questionMap[1]!['Monitoring'] = [
    'Habt ihr WLAN Zugang?', //
    'Wie habt ihr die Wirkungsmessung durchgeführt? Computerräume, Handy,..',
    'Was kann man nächstes mal besser machen?',
    'Positive aussagekräftige Evaluationsergebnisse',
    'Negative aussagekräftige Evaluationsergebnisse',
    'Beschlossene Maßnahmen',
    'Wie wurden die Ergebnisse mit den ÜL kommuniziert und besprochen',
  ];

  // questionMap[1]!['US-Workshops in der LF'] = [
  //   '??', //
  // ];

  questionMap[1]!['Stammtische'] = [
    'Wie viele ÜL aus dem Schulteam nehmen teil(z.B. 6 von 8)?', //
  ];

  questionMap[1]!['Weihnachtsaktion'] = [
    'Was hat das Schulteam sich überlegt?', //
  ];

  // 3

  questionMap[2]!['Planunsgespräche'] = [
    'Das Gespräch hat statt gefunden am:', //
    'Anwesend waren:',
    'Welches Gefühl hattest du nach dem Gespräch?',
  ];

  questionMap[2]!['Zeugnisse - HJ Wechsel'] = [
    'Wurde die Zeugnisvergabe zu Akquisezwecken genutzt? Wenn ja, wie?', //
    'Welche Jahrgangsstufen wurden angesprochen?',
  ];

  questionMap[2]!['Chancenreise: OS-Akquise'] = [
    'Welche Jahrgangsstufen wurden angesprochen?', //
    'Wann wurden die Schüler:innen im Zuge der Chancenreise angesprochen?',
    'Wann fand die Kick-Off Veranstatung statt?',
    'Wann fand der erste US-Workshop statt?',
  ];

  questionMap[2]!['US-Akquise'] = [
    'Welche Jahrgangsstufen wurden angesprochen?', //
    'In welchem Format wurden die Jahrgangsstufen angesprochen?',
  ];

  questionMap[2]!['Wirkungsmessung'] = [
    'Arbeit SchuKo: Hat sich die SchuKo gut um die Vorbereitung  der Evaulation gekümmert, sodass die Evaluation zügig und vollständig durchgeführt werden konnte? Wenn ja, woran erkennbar. Wenn nein, nenne Gründe ', //
    'Was kann man nächstes mal besser machen?',
    'Positive aussagekräftige Evaluationsergebnisse',
    'Negative aussagekräftige Evaluationsergebnisse',
    'Beschlossene Maßnahmen',
    'Wie wurden die Ergebnisse mit den ÜL kommuniziert und besprochen',
    'Habt ihr WLAN Zugang?',
    'Wie habt ihr die Wirkungsmessung durchgeführt? Computerräume, Handy,..',
  ];

  // questionMap[2]!['Stammtisch'] = [
  //   '??', //
  // ];

  questionMap[2]!['US-Workshops'] = [
    'Wann fand der Us Workshop statt?', //
  ];

  questionMap[2]!['Osteraktion'] = [
    'Welche Osteraktion wurde organisiert?', //
  ];

  // 4

  questionMap[3]!['Elternsprechtag'] = [
    'Wann fand der Elternsprechtag statt?', //
    'In welchen Format fand der Elternsprechtag statt?',
  ];

  questionMap[3]!['Chancenreise'] = [
    'Wann wurden die 8. Klässler im Zuge der Chancenreise angesprochen?', //
    'Wann fand die Kick-Off Veranstatung statt?',
    'Wann fand der erste US-Workshop statt?',
    'Wann fand der zweite US-Workshop statt?',
  ];

  // questionMap[3]!['Zertifikate OS'] = [
  //   '??', //
  // ];

  questionMap[3]!['Kennenlernnachmittage'] = [
    'Der Kennenlernnachmittag fand statt am:', //
    'In welchem Format fand der Kennenlernnachmittag statt?',
  ];

  questionMap[3]!['Planunsgespräche'] = [
    'Das Gespräch hat statt gefunden am:', //
    'Anwesend waren:',
    'Welches Gefühl hattest du nach dem Gespräch?',
  ];

  questionMap[3]!['Schulfeste'] = [
    'Haben wir am Sommerfest teilgenommen und in welchem Format (Infostand, ..)?', //
  ];

  questionMap[3]!['Ende der LF'] = [
    'Welche Ferienaktion wurde organisiert?', //
    'Wie habt ihr die Dankeskartenaktion durchgeführt?',
    'Wann beginnt die LF und ist dies an das Schulteam, die EZB kommuniziert?',
    'Wie kommuniziert ihr den Start?',
  ];

  questionMap[3]!['SchuKo-Event'] = [
    'Nimmt die SchuKo am Event teil? Wenn ja, was wurde organisiert?', //
  ];

  return questionMap;
}

function _getQuestionCount(category: string, quartal?: number) {
  if (quartal === undefined) {
    return _getQuestionMap()[category]?.length ?? 0;
  }
  return _getQuestionMapByQuartal()[quartal]?.[category]?.length ?? 0;
}

function _generateNewQuestionsForDocumentation(documentationID: string, category: string, year: Maybe<string>) {
  if (year?.includes('Q')) {
    const quartal = parseInt(year.split('Q')[1] ?? '1') - 1;
    return _getQuestionMapByQuartal()[quartal]?.[category].map(
      q => new QuarterNoteQuestion({ question: q, meetingNoteID: documentationID, category, year }),
    ) as QuarterNoteQuestion[];
  }

  return _getQuestionMap()[category].map(
    q => new QuarterNoteQuestion({ question: q, meetingNoteID: documentationID, category, year }),
  ) as QuarterNoteQuestion[];
}

const _subscribeQuarterNoteQuestionFiltered = registerSubscription({
  operationName: 'subscribeQuarterNoteQuestionFiltered',
  type: () => [QuarterNoteQuestion],
  params: [{ type: () => QuarterNoteQuestionFilter, name: 'filter' }],
  resolve: (filter: QuarterNoteQuestionFilter) => (gqlFields: BaseQLSelectionSet<QuarterNoteQuestion>) => {
    const selector = filter.getMongoFilter();
    const handle = getCollectionHandle<QuarterNoteQuestion>(QuarterNoteQuestion._collectionName);
    return from(publishFilter(QuarterNoteQuestion._class)).pipe(
      switchMap(publishFilter => {
        return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
          ...filter.getMongoOptions().sort,
        });
      }),
      switchMap(docs => {
        if (!docs.length) {
          // If no things are present just make new ones
          if (!filter.category) return of([] as QuarterNoteQuestion[]);
          return of(_generateNewQuestionsForDocumentation(filter.meetingNoteID ?? '', filter.category, filter.year));
        }

        if (filter.year && filter.year.includes('Q')) {
          const quartal = parseInt(filter.year.split('Q')[1] ?? '1') - 1;
          const questions = _getQuestionMapByQuartal()[quartal]?.[filter.category ?? ''] ?? [];
          if (questions && docs.length !== questions.length) {
            for (const question of questions) {
              if (!docs.find(q => q.question === question)) {
                docs.push(
                  new QuarterNoteQuestion({
                    question,
                    meetingNoteID: filter.meetingNoteID,
                    category: filter.category,
                    year: filter.year,
                  }),
                );
              }
            }
          }
        } else {
          // Insert possible new ones. Old ones are not deleted, we want to keep them
          if (filter.category && docs.length !== _getQuestionMap()[filter.category].length) {
            const questions = _getQuestionMap()[filter.category];
            for (const question of questions) {
              if (!docs.find(q => q.question === question)) {
                docs.push(
                  new QuarterNoteQuestion({
                    question,
                    meetingNoteID: filter.meetingNoteID,
                    category: filter.category,
                    year: filter.year,
                  }),
                );
              }
            }
          }
        }

        return of(docs);
      }),
      catchError(e => {
        return of([] as QuarterNoteQuestion[]);
      }),
    );
  },
});

const _subscribeStudentContactNotes = registerSubscription({
  operationName: 'subscribeStudentContactNotes',
  type: () => [StudentContactNote],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => String, name: 'studentID' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve:
    (skip: number, limit: number, studentID: string, filter?: AdornisFilter) =>
    (gqlFields: BaseQLSelectionSet<StudentContactNote>) => {
      const selector = { ...(studentID ? { studentID } : {}), ...(filter ? filter.getMongoFilter() : {}) };
      const handle = getCollectionHandle<StudentContactNote>(StudentContactNote._collectionName);
      return from(publishFilter(StudentContactNote._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
            sort: { date: 1 },
          });
        }),
        catchError(e => {
          return of([] as StudentContactNote[]);
        }),
      );
    },
});

const _subscribeStudentContactNotesKPF = registerSubscription({
  operationName: 'subscribeStudentContactNotesKPF',
  type: () => [StudentContactNote],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => String, name: 'kpfID' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve:
    (skip: number, limit: number, kpfID: string, filter?: AdornisFilter) =>
    (gqlFields: BaseQLSelectionSet<StudentContactNote>) => {
      const selector = { ...(filter ? filter.getMongoFilter() : {}) };
      const handle = getCollectionHandle<StudentContactNote>(StudentContactNote._collectionName);
      return from(publishFilter(StudentContactNote._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
            sort: { date: 1 },
          });
        }),
        switchMap(async docs => {
          const col = await getRawCollection<StudentContactNote>(StudentContactNote._collectionName);
          return col
            .aggregate([
              {
                $lookup: { from: 'adornisuser-materialized', localField: 'studentID', foreignField: '_id', as: 'user' },
              },
              { $match: { 'user.activeRelations.objectID': kpfID } },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray() as Promise<StudentContactNote[]>;
        }),
        catchError(e => {
          return of([] as StudentContactNote[]);
        }),
      );
    },
});

const _getStudentContactNotes = registerQuery({
  operationName: 'getStudentContactNotes',
  type: () => [StudentContactNote],
  params: [{ type: () => String, name: 'studentID' }],
  resolve: (studentID: string) => async (gqlFields: BaseQLSelectionSet<StudentContactNote>) => {
    const selector = { studentID };
    const col = await getCollection<StudentContactNote>(StudentContactNote._class);
    const docs = await col.find(selector, { sort: { date: -1 } }).toArray();
    return docs as StudentContactNote[];
  },
});

export namespace DocumentationAPI {
  export const queryMeetingNotesFiltered = _queryMeetingNotesFiltered;
  export const subscribeQuarterNoteQuestionFiltered = _subscribeQuarterNoteQuestionFiltered;
  export const getQuestionCount = _getQuestionCount;

  export const subscribeStudentContactNotes = _subscribeStudentContactNotes;
  export const getStudentContactNotes = _getStudentContactNotes;
  export const subscribeStudentContactNotesKPF = _subscribeStudentContactNotesKPF;
}
